/**
 * Contact Page Script
 *
 * @package Spice_Theme
 */

/* global hbspt */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

/** SCSSの $sptb と合わせること */
const SP_BREAKPOINT = 1024;

/**
 * タブインジケーター（スライドピル）の生成と初期配置
 *
 * @param {HTMLElement} tabsContainer - タブコンテナ要素
 * @returns {HTMLElement|null} インジケーター要素（SP時はnull）
 */
function createTabIndicator(tabsContainer) {
  // SP: タブが縦並びのためインジケーター不要
  if (window.matchMedia(`(max-width: ${SP_BREAKPOINT}px)`).matches) {
    return null;
  }

  const indicator = document.createElement('span');
  indicator.className = 'p-contact__tab-indicator';
  indicator.setAttribute('aria-hidden', 'true');
  tabsContainer.appendChild(indicator);

  const activeTab = tabsContainer.querySelector('.p-contact__tab.is-active');
  if (activeTab) {
    moveIndicator(indicator, tabsContainer, activeTab, false);
  }

  return indicator;
}

/**
 * インジケーターをタブ位置へ移動
 *
 * @param {HTMLElement} indicator      - インジケーター要素
 * @param {HTMLElement} tabsContainer  - タブコンテナ要素
 * @param {HTMLElement} tab            - 移動先タブ要素
 * @param {boolean}     animate        - アニメーションするか（false = 初期配置）
 */
function moveIndicator(indicator, tabsContainer, tab, animate) {
  if (!animate) {
    indicator.style.transition = 'none';
  }

  const tabRect = tab.getBoundingClientRect();
  const containerRect = tabsContainer.getBoundingClientRect();

  indicator.style.width = `${tabRect.width}px`;
  indicator.style.height = `${tabRect.height}px`;
  indicator.style.top = `${tabRect.top - containerRect.top}px`;
  indicator.style.transform = `translateX(${tabRect.left - containerRect.left}px)`;

  if (!animate) {
    // リフロー強制 → transition を元に戻す
    // eslint-disable-next-line no-unused-expressions
    indicator.offsetHeight;
    indicator.style.transition = '';
  }
}

/**
 * コンタクトページの初期化
 */
function initContactPage() {
  const tabsContainer = document.querySelector('.p-contact__tabs');
  const tabs = document.querySelectorAll('.p-contact__tab');
  const panels = document.querySelectorAll('.p-contact__panel');
  const intros = document.querySelectorAll('.p-contact__intro');
  const left = document.querySelector('.p-contact__left');

  // タブまたはコンテナが存在しない場合は終了
  if (tabs.length === 0 || !tabsContainer) {
    return;
  }

  // スライドインジケーター生成
  const indicator = createTabIndicator(tabsContainer);

  // リサイズ時にインジケーターを再配置（アニメーションなし）
  // AbortController でリスナーのライフサイクルを管理
  const resizeController = new AbortController();
  let resizeTimer;
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!indicator) return;
        if (window.matchMedia(`(max-width: ${SP_BREAKPOINT}px)`).matches) {
          indicator.style.display = 'none';
          return;
        }
        indicator.style.display = '';
        const active = tabsContainer.querySelector('.p-contact__tab.is-active');
        if (active) {
          moveIndicator(indicator, tabsContainer, active, false);
        }
      }, 100);
    },
    { signal: resizeController.signal }
  );

  /**
   * タブを指定キーに切り替える
   * @param {HTMLElement} tabEl - タブ要素
   */
  function switchToTab(tabEl) {
    const targetTab = tabEl.getAttribute('data-tab');

    tabs.forEach(t => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    tabEl.classList.add('is-active');
    tabEl.setAttribute('aria-selected', 'true');

    // インジケーターをスライド
    if (indicator) {
      moveIndicator(indicator, tabsContainer, tabEl, true);
    }

    panels.forEach(panel => {
      if (panel.getAttribute('data-tab') === targetTab) {
        panel.classList.add('is-active');
      } else {
        panel.classList.remove('is-active');
      }
    });

    const currentIntro = Array.from(intros).find(
      intro => intro.style.display !== 'none'
    );

    const showNextIntro = () => {
      intros.forEach(intro => {
        if (intro.getAttribute('data-tab') === targetTab) {
          intro.style.display = '';
          gsap.fromTo(
            intro,
            { opacity: 0 },
            { opacity: 1, duration: 0.3, ease: 'power2.out' }
          );
        } else {
          intro.style.display = 'none';
        }
      });
    };

    if (currentIntro) {
      gsap.to(currentIntro, {
        opacity: 0,
        duration: 0.2,
        ease: 'power2.out',
        onComplete: showNextIntro,
      });
    } else {
      showNextIntro();
    }

    const url = new URL(window.location);
    url.searchParams.set('tab', targetTab);
    window.history.replaceState({}, '', url);
  }

  // タブ切り替え（クリック）
  tabs.forEach(tab => {
    tab.addEventListener('click', function () {
      switchToTab(this);
    });
  });

  // タブ切り替え（キーボード: Enter / Space）
  tabs.forEach(tab => {
    tab.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        switchToTab(this);
      }
    });
  });

  // HubSpotフォームを初期化
  initHubSpotForms();

  return () => resizeController.abort();
}

/**
 * HubSpotフォームの初期化
 */
function initHubSpotForms() {
  // グローバル変数 spiceContactData が存在するか確認
  if (typeof window.spiceContactData === 'undefined') {
    return;
  }

  const hubspotForms = window.spiceContactData.hubspotForms;

  // hbsptが読み込まれるまで待機
  if (typeof hbspt === 'undefined') {
    setTimeout(initHubSpotForms, 100);
    return;
  }

  // 各フォームを初期化（cssを空にしてデフォルトスタイルを無効化、SCSSで上書き）
  for (const key in hubspotForms) {
    if (Object.prototype.hasOwnProperty.call(hubspotForms, key)) {
      const formConfig = {
        ...hubspotForms[key],
        css: '',
      };
      hbspt.forms.create(formConfig);
    }
  }
}

// DOMContentLoaded で初期化
document.addEventListener('DOMContentLoaded', () => {
  initContactPage();

  const contactEl = document.querySelector('.p-contact');
  if (
    contactEl &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    gsap.to(contactEl, {
      opacity: 1,
      ease: 'power3.out',
      duration: 0.8,
      delay: 0.2,
    });
  } else if (contactEl) {
    gsap.set(contactEl, { opacity: 1 });
  }
});
